/**
 * Announcement Banner Component
 * Handles cookie-based dismissal with content hash tracking
 */

class AnnouncementBanner {
    constructor() {
        this.banner = document.getElementById('announcement-banner');
        this.cookieName = 'announcement_banner_dismissed';
        this.cookieExpiryDays = 365; // 1 year

        if (!this.banner) {
            return;
        }

        this.contentHash = this.banner.getAttribute('data-content-hash');
        this.init();
    }

    init() {
        // Check if banner should be displayed
        if (this.shouldShowBanner()) {
            this.showBanner();
            this.attachCloseHandler();
        } else {
            this.hideBanner();
        }
    }

    /**
     * Generate a simple hash from string (for cookie comparison)
     * This is a simple hash function - in production you might want to use a more robust solution
     */
    generateHash(str) {
        let hash = 0;
        if (str.length === 0) return hash;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(36);
    }

    /**
     * Get cookie value by name
     */
    getCookie(name) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    /**
     * Set cookie with expiry date
     */
    setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = 'expires=' + date.toUTCString();
        document.cookie = name + '=' + value + ';' + expires + ';path=/;SameSite=Lax';
    }

    /**
     * Check if banner should be shown
     * Returns true if:
     * - No cookie exists, OR
     * - Cookie exists but content hash doesn't match (content changed)
     */
    shouldShowBanner() {
        const dismissedHash = this.getCookie(this.cookieName);
        
        // No cookie = show banner
        if (!dismissedHash) {
            return true;
        }

        // Cookie exists but hash doesn't match = content changed, show banner
        if (dismissedHash !== this.contentHash) {
            return true;
        }

        // Cookie exists and hash matches = previously dismissed, hide banner
        return false;
    }

    /**
     * Show the banner
     */
    showBanner() {
        if (this.banner) {
            this.banner.classList.add('is-visible');
            this.banner.setAttribute('aria-hidden', 'false');
        }
    }

    /**
     * Hide the banner
     */
    hideBanner() {
        if (this.banner) {
            this.banner.classList.remove('is-visible');
            this.banner.setAttribute('aria-hidden', 'true');
        }
    }

    /**
     * Handle close button click
     */
    handleClose() {
        // Store content hash in cookie
        this.setCookie(this.cookieName, this.contentHash, this.cookieExpiryDays);
        
        // Hide banner with animation
        this.hideBanner();
        
        // Dispatch custom event for tracking/analytics
        const event = new CustomEvent('announcementBanner:dismissed', {
            detail: {
                contentHash: this.contentHash
            }
        });
        document.dispatchEvent(event);
    }

    /**
     * Attach close button event handler
     */
    attachCloseHandler() {
        const closeButton = this.banner.querySelector('[data-banner-close]');
        if (closeButton) {
            closeButton.addEventListener('click', () => {
                this.handleClose();
            });
        }
    }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new AnnouncementBanner();
    });
} else {
    new AnnouncementBanner();
}

export default AnnouncementBanner;
